import { inject, Injectable, signal } from '@angular/core';
import {
  finalize,
  from,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
} from 'rxjs';
import { AuthService, OktaUserInfo } from '@brightside/core/auth';
import { ApiService, GenericResponseResult } from '@brighthub/api';
import { EmployeeSearchCacheService } from './employee-search-cache.service';

export interface EmployeeSearchPayload {
  payload: { users: OktaUserInfo[] };
}

export type EmployeeSearchResponse = EmployeeSearchPayload &
  GenericResponseResult;

@Injectable({
  providedIn: 'root',
})
export class EmployeeSearchService {
  private readonly _api = inject(ApiService);
  private readonly _auth = inject(AuthService);
  private readonly _cache = inject(EmployeeSearchCacheService);
  private readonly _inFlight = new Map<
    string,
    Observable<EmployeeSearchResponse>
  >();
  private _activeEmployee = signal<OktaUserInfo | null>(null);
  public readonly activeEmployee = this._activeEmployee.asReadonly();

  queryEmployee(search: string): Observable<EmployeeSearchResponse> {
    const query = search.trim();
    const userId = this._currentUserId();
    const cacheKey = `${userId ?? ''}::${query.toLowerCase()}`;
    const current = this._inFlight.get(cacheKey);
    if (current) return current;

    const request$ = from(this._cache.get(userId, query)).pipe(
      switchMap((cached) =>
        cached
          ? of(cached)
          : this._api
              .get<EmployeeSearchPayload>(
                'api-mobile',
                `/brightside-employee/user-search?query=${encodeURIComponent(query)}`,
              )
              .pipe(
                switchMap((response) =>
                  from(this._cache.set(userId, query, response)).pipe(
                    map(() => response),
                  ),
                ),
              ),
      ),
      finalize(() => this._inFlight.delete(cacheKey)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this._inFlight.set(cacheKey, request$);
    return request$;
  }

  findCachedEmployees(query: string): Observable<OktaUserInfo[]> {
    return from(this._cache.findUsers(this._currentUserId(), query));
  }

  selectEmployee(employeeInfo: OktaUserInfo) {
    this._activeEmployee.set(employeeInfo);
  }

  private _currentUserId(): string | null {
    const subject = this._auth.user()?.sub;
    if (typeof subject === 'string' && subject.trim()) return subject.trim();
    const decodedSubject = this._auth.getDecodedAccessToken()?.['sub'];
    return typeof decodedSubject === 'string' && decodedSubject.trim()
      ? decodedSubject.trim()
      : null;
  }
}
